import {Button, RemoveIcon} from 'epic-ue-ui';
import React, {InputHTMLAttributes, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Field, FieldInputProps, FieldMetaState} from 'react-final-form';
import {fileNotEmpty, required, toggleFileOption, validFileExt} from '../../jobForm.utils';
import {withLocalization} from '@epic-core/localization';

const Styled = styled.div`
    .file-selected-text {
        position: relative;
        bottom: 7px;
        left: 10px;
        display: inline-block;
        font-size: 16px;
        cursor: default;
    }

    .ue-icon-remove {
        width: 5%;
        height: auto;
        margin-left: 10px;
    }
`;

interface Props extends InputHTMLAttributes<HTMLInputElement> {
    name: string;
    isRequired?: boolean;
    uploadFileLabel?: string;
    noFileSelectedLabel?: string;
    labelAlphaNumeric?: string;
    isDisabled?: boolean;
    meta: FieldMetaState<any>;
}

interface InputProps {
    input: FieldInputProps<FileList, HTMLInputElement>;
    isRequired?: boolean;
    uploadFileLabel?: string;
    noFileSelectedLabel?: string;
    labelAlphaNumeric?: string;
    isDisabled?: boolean;
    meta?: FieldMetaState<any>;
    getMessage: (key: string) => string;
}

const FileInput = withLocalization(
    ({
        getMessage,
        input: {value, ...input},
        uploadFileLabel = getMessage('epic.jobs.form.uploadFile'),
        noFileSelectedLabel = getMessage('epic.jobs.form.noFileSelected'),
        meta,
        labelAlphaNumeric,
        isDisabled = false,
        ...props
    }: InputProps) => {
        const [fileSelected, setFileSelected] = useState(noFileSelectedLabel);
        const hiddenFileInput = React.useRef<HTMLInputElement>(null);
        const handleClick = (event) => {
            event.preventDefault();
            toggleFileOption(labelAlphaNumeric);
            hiddenFileInput?.current?.click();
        };
        const removeFile = () => {
            if (hiddenFileInput && hiddenFileInput.current) {
                hiddenFileInput.current.value = '';
            }
            input.onChange(null);
            setFileSelected(noFileSelectedLabel);
        };

        useEffect(() => {
            if (!value) {
                setFileSelected(noFileSelectedLabel);
            }
        }, [value]);

        return (
            <Styled className="custom-file-upload">
                <div
                    role="button"
                    tabIndex={0}
                    style={{display: 'contents'}}
                    onBlur={() => {
                        input.onBlur(); // trigger meta.touched
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleClick(e);
                        }
                    }}>
                    <Button tabIndex={-1} onClick={handleClick} type="secondary" color="white">
                        {uploadFileLabel}
                    </Button>
                    <div className="file-selected-text">{fileSelected}</div>
                    {fileSelected !== noFileSelectedLabel && <RemoveIcon onClick={removeFile} />}
                </div>
                <input
                    {...input}
                    ref={hiddenFileInput}
                    type="file"
                    disabled={isDisabled}
                    style={{display: 'none'}}
                    onChange={({target}: any) => {
                        target?.files[0].name && setFileSelected(target.files[0].name);
                        input.onChange(target.files);
                        input.onBlur();
                    }}
                    {...props}
                />
            </Styled>
        );
    }
);

const FileField = ({name, isRequired, ...props}: Props) => {
    return (
        <Field<FileList>
            name={name}
            validate={(value) => {
                return value
                    ? fileNotEmpty(value) || validFileExt(value)
                    : isRequired
                    ? required(value)
                    : undefined;
            }}>
            {({input, meta}) => <FileInput {...props} input={input} meta={meta} />}
        </Field>
    );
};

export default FileField;
